import React, { useState, useEffect } from 'react';
import {
  Flex,
  Box,
  Panel,
  Text,
  theme,
  Toggle,
  Alert,
  Button,
} from '@xometry/xometry_loft';
import quotesApi from '../../../../services/quotes';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const QUOTE_NOTIFICATION_TYPES_CONFIG = {
  'subscribe-email-ready-for-review': {
    title: 'Ready for Review',
    description:
      'You will be notified via email when your team has a quote that is Ready for Review.',
  },
  'subscribe-email-review-approved': {
    title: 'Review Approved',
    description:
      'You will be notified via email when your quote review has been approved by the team.',
  },
  'subscribe-email-ready-to-order': {
    title: 'Ready to Order',
    description:
      'You will be notified via email when your team has a quote that is Ready to Order.',
  },
  'subscribe-email-quote-shared-with-org': {
    title: 'Quote Shared with Team',
    description:
      'You will be notified via email when a new quote is shared with your team.',
  },
  'subscribe-email-rfq-submitted': {
    title: 'Manual Quote Requested',
    description:
      'You will be notified via email when your team has requested a Manual Quote.',
  },
};

const ORDER_NOTIFICATION_TYPES_CONFIG = {
  'subscribe-email-order-confirmation': {
    title: 'Order Confirmation',
    description:
      'You will be notified via email when your team has placed an order.',
  },
  'subscribe-email-order-forecasted-ship-date-updated': {
    title: 'Order Ship Date Updated',
    description:
      "You will be notified via email when the ship date of any of your team's orders have been updated.",
  },
  'subscribe-email-order-shipped': {
    title: 'Order Shipped',
    description:
      'You will be notified via email when an order for your team has shipped.',
  },
  'subscribe-email-order-shipment-delayed': {
    title: 'Order Shipment Delayed',
    description:
      'You will be notified via email if a shipped order for your team is delayed while in transit.',
  },
  'subscribe-email-order-shipment-delivered': {
    title: 'Order Shipment Delivered',
    description:
      'You will be notified via email when an order for your team has been delivered.',
  },
};

const TOOL_NOTIFICATION_TYPES_CONFIG = {
  'subscribe-email-tool-confirmation': {
    title: 'Tool Shared with Team',
    description:
      'You will be notified via email when a tool is shared with your team.',
  },
};

const TogglesSkeleton = ({ length }: { length: number }) => (
  <>
    {Array.from({ length }).map((_, idx) => (
      <Flex key={idx} alignItems="center">
        <Skeleton width="56px" height="28px" />
        <Skeleton width="150px" height="24px" style={{ marginLeft: '8px' }} />
      </Flex>
    ))}
  </>
);

/**
 * This component allows users to configure their team notifications for quotes, orders, and tools.
 *
 * NOTE: If new types of team notifications (outside of quotes, orders, or tools) are added in the future,
 * be sure to update the logic in the `upsertOnboardingChecklist` mutation to include them when evaluating
 * the `enable_notifications` checklist item. Additionally, if a new notification key is introduced (even
 * within quotes, orders, or tools) and it is not prefixed with "subscribe-", it must also be handled
 * explicitly in the mutation logic.
 */
const TeamNotifications = ({
  userId,
  orgId,
  isOrgMember,
  pageReadOnly,
  onOnboardingActionCompleted,
}: {
  userId: string;
  orgId: string;
  isOrgMember: boolean;
  pageReadOnly: boolean;
  onOnboardingActionCompleted: () => void;
}) => {
  const [quoteNotificationSettings, setQuoteNotificationSettings] =
    useState<any>({});
  const [
    isLoadingQuoteNotificationSettings,
    setIsLoadingQuoteNotificationSettings,
  ] = useState<boolean>(false);

  const [orderNotificationSettings, setOrderNotificationSettings] =
    useState<any>({});
  const [
    isLoadingOrderNotificationSettings,
    setIsLoadingOrderNotificationSettings,
  ] = useState<boolean>(false);

  const [toolNotificationSettings, setToolNotificationSettings] = useState<any>(
    {}
  );
  const [
    isLoadingToolNotificationSettings,
    setIsLoadingToolNotificationSettings,
  ] = useState<boolean>(false);

  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    const fetchQuoteOrgNotificationSettings = async () => {
      setIsLoadingQuoteNotificationSettings(true);
      try {
        const getQuoteNotificationSettings =
          await quotesApi.getQuoteOrgSettings(userId, orgId);
        if (getQuoteNotificationSettings) {
          const result = {};
          getQuoteNotificationSettings.forEach((setting) =>
            Object.assign(result, {
              [setting.type]: setting.active,
            })
          );
          setQuoteNotificationSettings({ ...result });
        }
        setIsError(false);
      } catch (e) {
        console.error(e);
        setIsError(true);
      } finally {
        setIsLoadingQuoteNotificationSettings(false);
      }
    };

    const fetchOrderOrgNotificationSettings = async () => {
      setIsLoadingOrderNotificationSettings(true);
      try {
        const getOrderNotificationSettings =
          await quotesApi.getOrderOrgSettings(userId, orgId);
        if (getOrderNotificationSettings) {
          const result = {};
          getOrderNotificationSettings.forEach((setting) =>
            Object.assign(result, {
              [setting.type]: setting.active,
            })
          );
          setOrderNotificationSettings({ ...result });
        }
        setIsError(false);
      } catch (e) {
        console.error(e);
        setIsError(true);
      } finally {
        setIsLoadingOrderNotificationSettings(false);
      }
    };

    const fetchToolOrgNotificationSettings = async () => {
      setIsLoadingToolNotificationSettings(true);
      try {
        const getToolNotificationSettings = await quotesApi.getToolOrgSettings(
          userId,
          orgId
        );
        if (getToolNotificationSettings) {
          const result = {};
          getToolNotificationSettings.forEach((setting) =>
            Object.assign(result, {
              [setting.type]: setting.active,
            })
          );
          setToolNotificationSettings({ ...result });
        }
        setIsError(false);
      } catch (e) {
        console.error(e);
        setIsError(true);
      } finally {
        setIsLoadingToolNotificationSettings(false);
      }
    };

    fetchQuoteOrgNotificationSettings();
    fetchOrderOrgNotificationSettings();
    fetchToolOrgNotificationSettings();
  }, []);

  const handleToggleNotification = async (
    entity: 'quote' | 'order' | 'tool',
    type: string,
    toggleBoolean: boolean
  ) => {
    if (!isOrgMember || pageReadOnly) return;

    if (entity === 'quote') {
      try {
        setQuoteNotificationSettings({
          ...quoteNotificationSettings,
          ...{
            [type]: toggleBoolean,
          },
        });

        await quotesApi.setQuoteOrgSetting(userId, orgId, type, toggleBoolean);
        setIsError(false);

        // Call onboarding callback when notification is enabled
        if (toggleBoolean) {
          onOnboardingActionCompleted();
        }
      } catch {
        // Setting wasn't successful so revert
        setQuoteNotificationSettings({
          ...quoteNotificationSettings,
          ...{
            [type]: !toggleBoolean,
          },
        });
        setIsError(true);
      }
    } else if (entity === 'order') {
      try {
        setOrderNotificationSettings({
          ...orderNotificationSettings,
          ...{
            [type]: toggleBoolean,
          },
        });

        await quotesApi.setOrderOrgSetting(userId, orgId, type, toggleBoolean);

        setIsError(false);

        // Call onboarding callback when notification is enabled
        if (toggleBoolean && onOnboardingActionCompleted) {
          onOnboardingActionCompleted();
        }
      } catch (err) {
        setOrderNotificationSettings({
          ...orderNotificationSettings,
          ...{
            [type]: !toggleBoolean,
          },
        });
        setIsError(true);
      }
    } else if (entity === 'tool') {
      try {
        setToolNotificationSettings({
          ...toolNotificationSettings,
          ...{
            [type]: toggleBoolean,
          },
        });

        await quotesApi.setToolOrgSetting(userId, orgId, type, toggleBoolean);

        setIsError(false);

        // Call onboarding callback when notification is enabled
        if (toggleBoolean && onOnboardingActionCompleted) {
          onOnboardingActionCompleted();
        }
      } catch (err) {
        setToolNotificationSettings({
          ...toolNotificationSettings,
          ...{
            [type]: !toggleBoolean,
          },
        });
        setIsError(true);
      }
    }
    window.analytics?.track('Teamspace Notification Toggled', {
      type,
      orgId,
      enabled: toggleBoolean,
    });
  };

  return (
    <Box>
      <Flex
        marginBottom={theme.space[4]}
        justifyContent="space-between"
        alignItems="center"
      >
        <Text kind="subpage-title">Notifications</Text>
      </Flex>
      <Panel kind="primary" lineHeight="24px">
        <Flex gap={theme.space[6]} flexDirection="column" marginBottom="32px">
          <Text kind="body-title">Quote Notifications</Text>
          {isLoadingQuoteNotificationSettings ? (
            <TogglesSkeleton
              length={Object.keys(QUOTE_NOTIFICATION_TYPES_CONFIG).length}
            />
          ) : (
            Object.entries(QUOTE_NOTIFICATION_TYPES_CONFIG).map(
              ([type, config]) => {
                const active =
                  type in quoteNotificationSettings
                    ? !!quoteNotificationSettings[type]
                    : false;
                return (
                  <Toggle
                    key={type}
                    handleChange={() =>
                      handleToggleNotification('quote', type, !active)
                    }
                    checked={active}
                    disabled={pageReadOnly}
                    label={
                      <Flex flexDirection="column">
                        <Text>{config.title}</Text>
                        <Text kind="body-sm" style={{ whiteSpace: 'normal' }}>
                          {config.description}
                        </Text>
                      </Flex>
                    }
                  />
                );
              }
            )
          )}
        </Flex>
        <Flex gap={theme.space[6]} flexDirection="column" marginBottom="32px">
          <Text kind="body-title">Order Notifications</Text>
          {isLoadingOrderNotificationSettings ? (
            <TogglesSkeleton
              length={Object.keys(ORDER_NOTIFICATION_TYPES_CONFIG).length}
            />
          ) : (
            Object.entries(ORDER_NOTIFICATION_TYPES_CONFIG).map(
              ([type, config]) => {
                const active =
                  type in orderNotificationSettings
                    ? !!orderNotificationSettings[type]
                    : false;
                return (
                  <Toggle
                    key={type}
                    handleChange={() =>
                      handleToggleNotification('order', type, !active)
                    }
                    checked={active}
                    disabled={pageReadOnly}
                    label={
                      <Flex flexDirection="column">
                        <Text>{config.title}</Text>
                        <Text kind="body-sm" style={{ whiteSpace: 'normal' }}>
                          {config.description}
                        </Text>
                      </Flex>
                    }
                  />
                );
              }
            )
          )}
        </Flex>
        <Flex gap={theme.space[6]} flexDirection="column">
          <Text kind="body-title">Tool Notifications</Text>
          {isLoadingToolNotificationSettings ? (
            <TogglesSkeleton
              length={Object.keys(TOOL_NOTIFICATION_TYPES_CONFIG).length}
            />
          ) : (
            Object.entries(TOOL_NOTIFICATION_TYPES_CONFIG).map(
              ([type, config]) => {
                const active =
                  type in toolNotificationSettings
                    ? !!toolNotificationSettings[type]
                    : false;
                return (
                  <Toggle
                    key={type}
                    handleChange={() =>
                      handleToggleNotification('tool', type, !active)
                    }
                    checked={active}
                    disabled={pageReadOnly}
                    label={
                      <Flex flexDirection="column">
                        <Text>{config.title}</Text>
                        <Text kind="body-sm" style={{ whiteSpace: 'normal' }}>
                          {config.description}
                        </Text>
                      </Flex>
                    }
                  />
                );
              }
            )
          )}
        </Flex>
        {isError && (
          <Alert isOpen={isError} kind={theme.kind.danger}>
            <Flex width="100%" justifyContent="space-between">
              <Text alignSelf="center">
                Something went wrong! Please try again.
              </Text>
              <Button
                title="dismiss"
                onClick={() => setIsError(false)}
                kind="transparent-dark"
                icon
                scale="small"
              >
                <i className="fa-solid fa-x" />
              </Button>
            </Flex>
          </Alert>
        )}
      </Panel>
    </Box>
  );
};

TeamNotifications.defaultProps = {
  userId: null,
  orgId: null,
  isOrgMember: true,
};

export default TeamNotifications;
